import { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Checkbox } from "antd";
import { signUpApi, toAuthResult } from "@/portals/patient/api/patientAuthApi";
import { getInvitePreviewApi } from "@/portals/patient/api/familyApi";
import { saveAuthFlowState } from "./authFlowState";
import { InviteUnavailableNotice } from "./InviteUnavailableNotice";
import { toastError, toastSuccess } from "@/shared/lib/toast";
import { AuthTextField, AuthPasswordField } from "@/shared/components/AuthField";
import { AuthSubmitButton } from "@/shared/components/AuthSubmitButton";
import { Button } from "@/shared/components/button";
import { validateFields } from "@/shared/lib/validators";
import { CONFIRM_PASSWORD_RULES, PASSWORD_MISMATCH_MESSAGE, SIGNUP_PASSWORD_RULES } from "@/shared/lib/passwordRules";

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" xmlns="http://www.w3.org/2000/svg">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  );
}

interface InviteContext {
  patientName: string;
  categories: string[];
}

export function PatientSignUpPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get("token");
  const [loading, setLoading] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [terms, setTerms] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [inviteContext, setInviteContext] = useState<InviteContext | null>(null);
  const [emailLocked, setEmailLocked] = useState(false);
  // True only when a token WAS present but didn't resolve to a live invite —
  // cancelled, declined, already used, or expired. Blocks the form entirely
  // instead of silently falling back to a normal signup: someone who clicked
  // a real invite email link deserves to be told what happened to it, not
  // land on a blank form with no explanation (see the branded message below).
  const [inviteUnavailable, setInviteUnavailable] = useState(false);
  const restoredValues = (location.state as { signupForm?: Record<string, unknown> } | null)?.signupForm;

  useEffect(() => {
    if (!restoredValues) return;
    if (typeof restoredValues.firstName === "string") setFirstName(restoredValues.firstName);
    if (typeof restoredValues.lastName === "string") setLastName(restoredValues.lastName);
    if (typeof restoredValues.email === "string") setEmail(restoredValues.email);
    if (typeof restoredValues.password === "string") setPassword(restoredValues.password);
    if (typeof restoredValues.confirmPassword === "string") setConfirmPassword(restoredValues.confirmPassword);
    if (typeof restoredValues.terms === "boolean") setTerms(restoredValues.terms);
  }, [restoredValues]);

  // Resolves a caregiver invite link's token so the email field can be
  // pre-filled and locked, and the inviting patient shown. A token that
  // doesn't resolve (cancelled, declined, already used, expired) shows the
  // dedicated "no longer available" message instead (see inviteUnavailable) —
  // never a silent fallback to a plain signup form for a real invite link.
  useEffect(() => {
    if (!inviteToken) return;
    let cancelled = false;
    getInvitePreviewApi(inviteToken).then(({ data }) => {
      if (cancelled) return;
      if (!data) {
        setInviteUnavailable(true);
        return;
      }
      setEmail(data.email);
      setEmailLocked(true);
      setInviteContext({ patientName: data.patientName, categories: data.categories });
    });
    return () => {
      cancelled = true;
    };
  }, [inviteToken]);

  const clearError = (field: string) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const { [field]: _removed, ...rest } = prev;
      return rest;
    });
  };

  const openLegalPage = (path: string) => {
    const signupForm = { firstName, lastName, email, password, confirmPassword, terms };
    navigate("/patient/auth/signup", { replace: true, state: { signupForm } });
    navigate(path, { state: { from: "/patient/auth/signup", signupForm } });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const fieldErrors = validateFields(
      { firstName, lastName, email, password, confirmPassword, terms },
      {
        firstName: [{ required: true, message: "First name is required" }],
        lastName: [{ required: true, message: "Last name is required" }],
        email: [
          { required: true, message: "Email is required" },
          { type: "email", message: "Enter a valid email address" },
        ],
        password: SIGNUP_PASSWORD_RULES,
        confirmPassword: CONFIRM_PASSWORD_RULES,
        terms: [{ validator: (value) => (value ? undefined : "Please accept the terms") }],
      },
    );
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) return;

    if (password !== confirmPassword) {
      toastError(PASSWORD_MISMATCH_MESSAGE);
      return;
    }

    setLoading(true);
    const result = toAuthResult(await signUpApi(firstName, lastName, email, password, confirmPassword));
    setLoading(false);

    if (result.success) {
      saveAuthFlowState({ email, fromSignup: true });
      toastSuccess("OTP has been sent successfully. Please check your email.");
      navigate("/patient/auth/verify-otp", { state: { email, fromSignup: true, inviteContext } });
    } else {
      // The backend's message for a duplicate account confirms the email is
      // registered — don't relay that account-enumeration signal to the user.
      const isDuplicateEmail = /already (?:exists|registered|in use)|account.*already/i.test(result.error ?? "");
      toastError(isDuplicateEmail ? "We can't proceed with this email right now. Please try a different email" : result.error ?? "Unable to create account.");
    }
  };

  if (inviteUnavailable) {
    return <InviteUnavailableNotice continueTo="/patient/auth/signup" continueLabel="Continue to a regular sign up" />;
  }

  return (
    <div className="w-full max-w-sm">
      <h2 className="!font-normal text-[28px] leading-tight text-[#0A2947]">
        Welcome To <span className="font-extrabold tracking-tight">MedSyncIQ</span>
      </h2>
      <p className="!mt-2 !mb-9 text-sm text-[#9CA3AF]">Please enter your details</p>

      {inviteContext && (
        <div className="!mb-6 flex items-start gap-2.5 rounded-xl border border-[#F36F8F]/40 bg-[#FFF7F8] p-3.5">
          <Icon icon="lucide:heart-handshake" className="mt-0.5 h-4 w-4 shrink-0 text-[#E4002B]" />
          <p className="text-xs leading-relaxed text-[#374151]">
            <span className="font-semibold">{inviteContext.patientName}</span> invited you to view their health records
            {inviteContext.categories.length > 0 && (
              <> — <span className="font-medium">{inviteContext.categories.join(", ")}</span></>
            )}
            . Finish setting up your account to accept.
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="mb-5 grid grid-cols-2 gap-3">
          <AuthTextField
            label="First Name"
            placeholder="John"
            autoComplete="given-name"
            icon="lucide:user-round"
            value={firstName}
            onChange={(v) => { setFirstName(v); clearError("firstName"); }}
            error={errors.firstName}
            className="mb-0"
          />
          <AuthTextField
            label="Last Name"
            placeholder="Doe"
            autoComplete="family-name"
            icon="lucide:user-round"
            value={lastName}
            onChange={(v) => { setLastName(v); clearError("lastName"); }}
            error={errors.lastName}
            className="mb-0"
          />
        </div>

        <AuthTextField
          label="Email address"
          placeholder="john.doe@example.com"
          autoComplete="email"
          icon="lucide:mail"
          value={email}
          onChange={(v) => { setEmail(v); clearError("email"); }}
          error={errors.email}
          disabled={emailLocked}
        />

        <AuthPasswordField
          label="Password"
          placeholder="Password"
          autoComplete="new-password"
          icon="lucide:lock"
          value={password}
          onChange={(v) => { setPassword(v); clearError("password"); }}
          error={errors.password}
        />

        <AuthPasswordField
          label="Confirm Password"
          placeholder="Confirm Password"
          autoComplete="new-password"
          icon="lucide:lock"
          value={confirmPassword}
          onChange={(v) => { setConfirmPassword(v); clearError("confirmPassword"); }}
          error={errors.confirmPassword}
        />

        <div className="mb-6">
          <div className="flex items-center gap-2">
            <Checkbox
              checked={terms}
              onChange={(e) => { setTerms(e.target.checked); clearError("terms"); }}
              className="[&_.ant-checkbox]:!rounded-full [&_.ant-checkbox]:!border-[#E4002B]/50 [&_.ant-checkbox-checked]:!bg-[#E4002B]/50 [&_.ant-checkbox-checked]:!border-[#E4002B]/50"
            />
            <span className="text-[13px] text-[#6F7280]">
              I agree to the{" "}
              <Button
                variant="link"
                onClick={() => openLegalPage("/terms-of-use")}
                className="!h-auto !p-0 !font-semibold !text-[#E4002B] !underline !underline-offset-2 hover:!text-[#E4002B]"
              >
                Terms of Use
              </Button>{" "}
              and{" "}
              <Button
                variant="link"
                onClick={() => openLegalPage("/privacy-policy")}
                className="!h-auto !p-0 !font-semibold !text-[#E4002B] !underline !underline-offset-2 hover:!text-[#E4002B]"
              >
                Privacy Policy
              </Button>
            </span>
          </div>
          {errors.terms && <p className="!mt-1 text-[11px] text-[#E4002B]">{errors.terms}</p>}
        </div>

        <div className="flex flex-col gap-5">
          <AuthSubmitButton loading={loading}>Sign Up</AuthSubmitButton>

          <Button
            variant="outline"
            className="!flex !h-auto !w-full !items-center !justify-center !gap-2.5 !rounded-xl !border-[#E5E7EB] !bg-white !py-3 !text-sm !font-medium !text-[#374151] hover:!border-[#E5E7EB] hover:!bg-[#F9FAFB] hover:!text-[#374151]"
          >
            <GoogleIcon />
            Sign up with Google
          </Button>
        </div>
      </form>

      <p className="!mt-5 text-center text-sm text-[#9CA3AF]">
        Already have an account?{" "}
        <Link to="/patient/auth/signin" className="font-semibold text-[#E4002B] hover:underline">
          Sign in
        </Link>
      </p>
    </div>
  );
}
